import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
	Box,
	Button,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { useAuthContext } from '../../../../../context/auth';
import { dayjs } from '../../../../../date-util';
import ScriptLogsAPI from '../../api/scriptLogs';
import {
	SCRIPT_LOGS_FETCH_MORE_TEXT,
	SCRIPT_LOGS_HAS_MORE_TEXT,
	SCRIPT_LOGS_NESTED_CLOSE_TEXT,
	SCRIPT_LOGS_NESTED_HEADER,
	SCRIPT_LOGS_NO_MORE_TEXT,
	SCRIPT_LOGS_PLUGIN_SUBHEADER,
	SCRIPT_LOGS_ROW_DATE_FORMAT,
	SCRIPT_LOGS_SUBHEADER_TIMESTAMP,
} from '../../data/constants';
import ScriptLogRowNested from './ScriptLogRowNested';
import ScriptLogRowSkeleton from './ScriptLogRowSkeleton';

interface NestedScriptLogsProps {
	requestId: string;
	timestamp: string;
	message: string;
	tenantHostname?: string;
	handleClose(): void;
}

const ScriptLogsNested = (props: NestedScriptLogsProps) => {
	const { ref, inView } = useInView();
	const { requestId, timestamp, message, handleClose, tenantHostname } = props;

	const authData = useAuthContext();

	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;

	const {
		data: scriptLogs,
		isLoading,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
	} = ScriptLogsAPI.usePaginatedScriptLogs(
		['nestedScriptLogs', tenantId || tenantHostname, message],
		{
			requestId,
		},
		tenantId,
		authToken,
		tenantHostname,
		50,
	);
	React.useEffect(() => {
		if (inView) {
			fetchNextPage();
		}
	}, [fetchNextPage, inView]);

	return (
		<Stack direction='column' alignItems='flex-start' spacing={2} height='100%'>
			<Button
				startIcon={<ArrowBackIcon />}
				onClick={() => handleClose()}
				size='small'
				color='neutralButton'
			>
				{SCRIPT_LOGS_NESTED_CLOSE_TEXT()}
			</Button>
			<Typography fontWeight={700} variant='body1'>
				{SCRIPT_LOGS_NESTED_HEADER()} - {requestId}
			</Typography>
			<Stack direction='column' sx={{ m: 0 }}>
				<Typography
					variant='body1'
					gutterBottom
					color='black.darkest'
					fontWeight={500}
				>
					{dayjs(parseInt(timestamp)).format(SCRIPT_LOGS_ROW_DATE_FORMAT())}
				</Typography>
				<Box
					sx={{
						overflow: 'hidden',
						display: '-webkit-box',
						WebkitLineClamp: '3',
						WebkitBoxOrient: 'vertical',
						maxHeight: '10vh',
					}}
				>
					<Typography
						variant='body1'
						gutterBottom
						color='black.light'
						fontWeight={400}
					>
						{message}
					</Typography>
				</Box>
			</Stack>
			<Paper sx={{ flex: 1, overflowY: 'auto', width: '100%' }}>
				<TableContainer sx={{ maxHeight: '100%' }}>
					<Table size='small' stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{SCRIPT_LOGS_SUBHEADER_TIMESTAMP()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{SCRIPT_LOGS_PLUGIN_SUBHEADER()}
									</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading &&
								[...Array(3)].map((_, i) => <ScriptLogRowSkeleton key={i} />)}
							{!isLoading &&
								scriptLogs?.pages
									.map((logResponse) => logResponse.logEvents)
									.flat()
									.map((logEvent, index) => (
										<ScriptLogRowNested key={index} {...logEvent} />
									))}
							{!isLoading && (
								<TableRow ref={ref}>
									<TableCell colSpan={2} align='center'>
										<Typography>
											{isFetchingNextPage
												? SCRIPT_LOGS_FETCH_MORE_TEXT()
												: hasNextPage
												? SCRIPT_LOGS_HAS_MORE_TEXT()
												: SCRIPT_LOGS_NO_MORE_TEXT()}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</Stack>
	);
};

export default ScriptLogsNested;
